import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import PublicHeader from "@/components/PublicHeader";
import PublicFooter from "@/components/PublicFooter";
import { StatusIcon } from "@/components/StatusIcon";
import { Check, AlertCircle, Loader2, Flower2 } from "lucide-react";

const POLL_INTERVAL_MS = 2200;
const MAX_POLLS = 30; // ~66s

export default function CheckoutReturnPage() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const navigate = useNavigate();

  const [state, setState] = useState({
    status: "polling", // polling | paid | expired | error
    transaction: null,
    subscription: null,
    accountEmail: "",
    newlyCreated: false,
    needsPasswordSetup: false,
    error: "",
  });
  const pollsRef = useRef(0);
  const stopRef = useRef(false);

  useEffect(() => {
    if (!sessionId) {
      setState((s) => ({ ...s, status: "error", error: "Missing session_id." }));
      return;
    }
    let timer;
    const poll = async () => {
      if (stopRef.current) return;
      pollsRef.current += 1;
      try {
        const { data } = await api.get(`/checkout/status/${sessionId}`);
        if (data.payment_status === "paid") {
          stopRef.current = true;
          setState({
            status: "paid",
            transaction: data.transaction || null,
            subscription: data.subscription || null,
            accountEmail: data.account_email || "",
            newlyCreated: !!data.newly_created,
            needsPasswordSetup: !!data.needs_password_setup,
            error: "",
          });
          return;
        }
        if (data.session_status === "expired") {
          stopRef.current = true;
          setState((s) => ({ ...s, status: "expired" }));
          return;
        }
        if (pollsRef.current >= MAX_POLLS) {
          stopRef.current = true;
          setState((s) => ({ ...s, status: "error", error: "Payment is taking longer than expected. Please check your email or contact us." }));
          return;
        }
        timer = setTimeout(poll, POLL_INTERVAL_MS);
      } catch (e) {
        if (pollsRef.current >= MAX_POLLS) {
          stopRef.current = true;
          setState((s) => ({ ...s, status: "error", error: formatApiError(e.response?.data?.detail) || "Could not verify payment." }));
        } else {
          timer = setTimeout(poll, POLL_INTERVAL_MS);
        }
      }
    };
    poll();
    return () => {
      stopRef.current = true;
      if (timer) clearTimeout(timer);
    };
  }, [sessionId]);

  return (
    <div className="club-theme min-h-screen" data-testid="checkout-return-page">
      <PublicHeader />
      <section className="max-w-3xl mx-auto px-6 md:px-12 py-20">
        {state.status === "polling" && (
          <div className="text-center" data-testid="checkout-polling">
            <Loader2 className="mx-auto mb-4 text-[var(--bloom-brand)] animate-spin" strokeWidth={1.5} size={36} />
            <h1 className="font-serif text-4xl mb-3">Confirming your payment…</h1>
            <p className="font-serif italic text-[var(--bloom-ink-soft)]">
              Hold tight, we're just chatting with Stripe.
            </p>
          </div>
        )}

        {state.status === "paid" && (
          <PaidView state={state} sessionId={sessionId} navigate={navigate} />
        )}

        {state.status === "expired" && (
          <div className="text-center" data-testid="checkout-expired">
            <AlertCircle className="mx-auto mb-4 text-[var(--bloom-warning)]" strokeWidth={1.5} size={36} />
            <h1 className="font-serif text-4xl mb-3">Checkout expired</h1>
            <p className="font-serif italic text-[var(--bloom-ink-soft)] mb-6">
              No worries, nothing was charged. You can try again.
            </p>
            <Link to="/subscribe" className="bloom-btn bloom-btn-primary" data-testid="checkout-retry">Try again</Link>
          </div>
        )}

        {state.status === "error" && (
          <div className="text-center" data-testid="checkout-error">
            <AlertCircle className="mx-auto mb-4 text-[var(--bloom-accent)]" strokeWidth={1.5} size={36} />
            <h1 className="font-serif text-4xl mb-3">We hit a snag</h1>
            <p className="font-serif italic text-[var(--bloom-ink-soft)] mb-6">{state.error}</p>
            <Link to="/subscribe" className="bloom-btn bloom-btn-outline">Back to the wizard</Link>
          </div>
        )}
      </section>
      <PublicFooter />
    </div>
  );
}

function PaidView({ state, sessionId, navigate }) {
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);

  const setPasswordSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    if (password.length < 6) return setErr("Password must be at least 6 characters.");
    if (password !== confirm) return setErr("Passwords don't match.");
    setBusy(true);
    try {
      await api.post("/checkout/set-password", { session_id: sessionId, password });
      setDone(true);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || "Could not set password.");
    } finally {
      setBusy(false);
    }
  };

  const sub = state.subscription || {};
  const tierIconKey = sub.plan_size ? `subscription.${sub.plan_size}` : null;
  return (
    <div data-testid="checkout-paid">
      <div className="text-center mb-10">
        <StatusIcon
          name="badge.founding_member"
          size={96}
          className="mx-auto mb-4"
          fallback={<Check className="mx-auto mb-4 text-[var(--bloom-brand)]" strokeWidth={1.5} size={42} />}
        />
        <h1 className="font-serif text-5xl mb-3">Welcome to The Bloom Club.</h1>
        <p className="font-serif italic text-xl text-[var(--bloom-ink-soft)]">
          Payment confirmed. We've sent a receipt to <strong className="not-italic">{state.accountEmail}</strong>.
        </p>
      </div>

      <div className="bloom-card p-6 md:p-8 mb-8" data-testid="paid-summary">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="text-[10px] tracking-[0.25em] uppercase text-[var(--bloom-ink-muted)] mb-1">Your subscription</div>
            <div className="font-serif text-2xl capitalize">{sub.plan_name}</div>
            <div className="font-serif italic text-[var(--bloom-ink-soft)]">{sub.duration_label} · {sub.delivery_count} {sub.delivery_count === 1 ? "delivery" : "deliveries"}</div>
            <div className="font-serif italic text-sm text-[var(--bloom-ink-muted)] mt-2">Season: {sub.season_name}</div>
          </div>
          {tierIconKey ? (
            <StatusIcon name={tierIconKey} size={64} fallback={<Flower2 className="text-[var(--bloom-accent)]" strokeWidth={1.2} size={48} />} />
          ) : (
            <Flower2 className="text-[var(--bloom-accent)]" strokeWidth={1.2} size={48} />
          )}
        </div>
        {sub.balance_due > 0 && (
          <div className="mt-4 pt-4 border-t border-[var(--bloom-border)] font-serif italic text-sm text-[var(--bloom-ink-soft)]">
            Balance of ${sub.balance_due.toFixed(2)} due closer to your first delivery. We'll be in touch.
          </div>
        )}
      </div>

      {state.needsPasswordSetup && !done && (
        <form onSubmit={setPasswordSubmit} className="bloom-card p-6 md:p-8" data-testid="set-password-form">
          <h2 className="font-serif text-2xl mb-2">Set your password</h2>
          <p className="font-serif italic text-[var(--bloom-ink-soft)] mb-6">
            Your subscriber account has been created. Choose a password so you can sign in and manage your subscription any time.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <div className="bloom-label mb-2">Password</div>
              <input type="password" className="bloom-input" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="set-password" />
            </div>
            <div>
              <div className="bloom-label mb-2">Confirm password</div>
              <input type="password" className="bloom-input" value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="set-password-confirm" />
            </div>
          </div>
          {err && (
            <div className="mt-4 flex items-center gap-2 text-sm text-[var(--bloom-accent)] font-serif italic">
              <AlertCircle size={14} /> {err}
            </div>
          )}
          <button type="submit" disabled={busy} className="bloom-btn bloom-btn-primary mt-6" data-testid="set-password-submit">
            {busy ? "Saving…" : "Save password and continue"}
          </button>
        </form>
      )}

      {done && (
        <div className="bloom-card p-6 md:p-8 text-center" data-testid="set-password-done">
          <Check className="mx-auto mb-3 text-[var(--bloom-brand)]" strokeWidth={1.5} size={32} />
          <h2 className="font-serif text-2xl mb-2">All set.</h2>
          <p className="font-serif italic text-[var(--bloom-ink-soft)] mb-6">You can now sign in to your subscriber portal.</p>
          <button onClick={() => navigate("/login")} className="bloom-btn bloom-btn-primary" data-testid="goto-login">Sign in</button>
        </div>
      )}

      {!state.needsPasswordSetup && !done && (
        <div className="text-center">
          <button onClick={() => navigate("/login")} className="bloom-btn bloom-btn-primary" data-testid="goto-login">Sign in to your portal</button>
        </div>
      )}
    </div>
  );
}
